import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Clock, Loader2, X } from 'lucide-react';
import { apiUrl } from '../lib/api.js';
import { formatCurrency, formatDate } from '../lib/format.js';
import { useCategories } from '../hooks/useCategories.js';
import { useConfirmReceipt, useDismissReceipt, useRejectReceipt } from '../hooks/useReceipts.js';
import CategoryCombobox from './CategoryCombobox.jsx';
import EntityBadge from './EntityBadge.jsx';
import ReceiptVatFields, { extractedVatState, vatPayloadFromState } from './ReceiptVatFields.jsx';

const MAX_CANDIDATES = 5;

function money(cents) {
  return cents == null ? '—' : formatCurrency(Math.abs(cents));
}

function vatSummary(ex) {
  const bd = ex?.vat_breakdown ?? [];
  if (bd.length > 0) {
    return bd.map((b) => `${b.rate ?? '?'} % : ${money(b.vat_amount_cents)}`).join(' · ');
  }
  if (ex?.vat_rate != null) {
    return `${ex.vat_rate} %${ex.vat_amount_cents != null ? ` : ${money(ex.vat_amount_cents)}` : ''}`;
  }
  if (ex?.vat_amount_cents != null) return money(ex.vat_amount_cents);
  return null;
}

function ExtractedSummary({ ex }) {
  if (!ex) return null;
  const vat = vatSummary(ex);
  return (
    <div className="rounded-xl bg-slate-50 p-3 text-sm">
      <p className="font-medium text-slate-900">{ex.merchant || ex.label || 'Ticket'}</p>
      <p className="mt-0.5 flex flex-wrap gap-x-3 text-slate-600">
        <span className="tabular-nums">{money(ex.amount_cents)}</span>
        {ex.date && (
          <span>{formatDate(ex.date)}{ex.time ? ` à ${ex.time}` : ''}</span>
        )}
      </p>
      {vat && <p className="mt-0.5 text-xs text-slate-500">TVA {vat}</p>}
    </div>
  );
}

function CandidateCard({ candidate, selected, onSelect, asButton }) {
  const body = (
    <>
      <div className="min-w-0 flex-1 text-left">
        <p className="truncate text-sm font-medium text-slate-900">
          {candidate.counterparty_name || candidate.label || 'Transaction'}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">{formatDate(candidate.operation_date)}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-sm font-semibold tabular-nums text-slate-900">{money(candidate.amount_cents)}</span>
        <EntityBadge name={candidate.entity_name} />
      </div>
    </>
  );
  const cls = `flex w-full items-center gap-3 rounded-xl border p-3 ${
    selected ? 'border-slate-800 bg-white ring-1 ring-slate-800' : 'border-slate-200 bg-white'
  }`;
  if (!asButton) return <div className={cls}>{body}</div>;
  return (
    <button type="button" aria-pressed={selected} onClick={onSelect} className={`${cls} min-h-14`}>
      {body}
    </button>
  );
}

function ConfirmForm({ receipt, onConfirmed, onDismiss, dismissing }) {
  const confirm = useConfirmReceipt();
  const reject = useRejectReceipt();
  const { data: catData } = useCategories();
  const candidates = (receipt.candidates ?? []).slice(0, MAX_CANDIDATES);
  const [chosenId, setChosenId] = useState(receipt.match_transaction_id ?? null);
  const [choosing, setChoosing] = useState(!receipt.match_transaction_id);
  const [categoryChoice, setCategoryChoice] = useState(undefined);
  const [vatOverride, setVatOverride] = useState(null);
  const ex = receipt.extracted;
  const vat = vatOverride ?? extractedVatState(ex);

  const selected = candidates.find((c) => c.transaction_id === chosenId) ?? null;
  const categoryId = categoryChoice ?? selected?.suggested_category_id ?? null;

  const categories = useMemo(() => {
    const all = Array.isArray(catData) ? catData : catData?.categories ?? catData?.data ?? [];
    return all.filter(
      (c) =>
        (c.entity_id == null || c.entity_id === selected?.entity_id) &&
        (c.flow_type == null || c.flow_type === 'expense')
    );
  }, [catData, selected?.entity_id]);

  function choose(id) {
    setChosenId(id);
    setChoosing(false);
    setCategoryChoice(undefined);
  }

  async function handleConfirm() {
    if (!selected) return;
    const vatFields = vatOverride ? vatPayloadFromState(vatOverride) : {};
    try {
      const result = await confirm.mutateAsync({
        id: receipt.id,
        transaction_id: selected.transaction_id,
        entity_id: selected.entity_id,
        ...(categoryId ? { category_id: categoryId } : {}),
        ...vatFields,
      });
      onConfirmed(result);
    } catch { /* erreur via confirm.error */ }
  }

  async function handleReject() {
    try {
      onConfirmed(await reject.mutateAsync(receipt.id));
    } catch { /* erreur via reject.error */ }
  }

  return (
    <div className="space-y-4">
      <ExtractedSummary ex={ex} />

      {choosing ? (
        <section aria-label="Choisir la transaction" className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900">
            {receipt.match_transaction_id ? 'Choisissez la bonne transaction' : 'Quelle transaction ?'}
          </h3>
          {candidates.map((c) => (
            <CandidateCard
              key={c.transaction_id}
              candidate={c}
              selected={c.transaction_id === chosenId}
              onSelect={() => choose(c.transaction_id)}
              asButton
            />
          ))}
          <button
            type="button"
            onClick={handleReject}
            disabled={reject.isPending}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white text-sm font-medium text-slate-700 active:bg-slate-50 disabled:opacity-50"
          >
            {reject.isPending ? <Loader2 size={16} className="animate-spin" aria-hidden /> : null}
            Aucune ne correspond
          </button>
          <p className="text-center text-xs text-slate-500">
            Le ticket reste en attente du bon paiement ; ces transactions ne lui seront plus proposées.
          </p>
          {reject.error && <p role="alert" className="text-sm text-red-600">{reject.error.message}</p>}
        </section>
      ) : (
        selected && (
          <>
            <section aria-label="Transaction rapprochée" className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-900">Transaction rapprochée</h3>
              <CandidateCard candidate={selected} />
              <button
                type="button"
                onClick={() => setChoosing(true)}
                className="min-h-11 text-sm text-slate-600 underline"
              >
                Ce n'est pas la bonne transaction
              </button>
              {selected.has_qonto_attachments && (
                <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-200">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
                  Cette transaction a déjà un justificatif
                </p>
              )}
            </section>

            <section className="space-y-1">
              <h3 className="text-xs font-medium text-slate-500">Catégorie</h3>
              <CategoryCombobox
                value={categoryId}
                onChange={(id) => setCategoryChoice(id ?? null)}
                categories={categories}
                placement="top"
              />
            </section>

            <ReceiptVatFields vat={vat} totalCents={ex?.amount_cents} onChange={setVatOverride} />

            {confirm.error && (
              <p role="alert" className="text-sm text-red-600">{confirm.error.message}</p>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirm.isPending}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-800 text-base font-semibold text-white active:bg-slate-900 disabled:bg-slate-400"
            >
              {confirm.isPending ? <Loader2 size={18} className="animate-spin" aria-hidden /> : null}
              Valider
            </button>
          </>
        )
      )}

      <DismissLink onDismiss={onDismiss} pending={dismissing} />
    </div>
  );
}

function DismissLink({ onDismiss, pending }) {
  return (
    <div className="text-center">
      <button
        type="button"
        onClick={onDismiss}
        disabled={pending}
        className="min-h-11 px-3 text-xs text-slate-400 underline disabled:opacity-50"
      >
        Supprimer ce ticket
      </button>
    </div>
  );
}

function SuccessState({ receipt, onClose }) {
  const applied = receipt.applied ?? {};
  const parts = ['Justificatif envoyé dans Qonto'];
  if (applied.vat_applied) parts.push('TVA appliquée');
  if (applied.category_applied) parts.push('catégorie appliquée');

  useEffect(() => {
    const t = setTimeout(onClose, 1500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700">
        <Check size={28} aria-hidden />
      </span>
      <p role="status" className="text-base font-semibold text-slate-900">{parts.join(' · ')}</p>
      <button
        type="button"
        onClick={onClose}
        className="min-h-12 w-full rounded-xl bg-slate-800 text-base font-semibold text-white"
      >
        OK
      </button>
    </div>
  );
}

function PendingState({ receipt, onClose, onDismiss, dismissing }) {
  const failed = !receipt.extracted;
  return (
    <div className="space-y-4">
      <ExtractedSummary ex={receipt.extracted} />
      {failed && (
        <p role="alert" className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-amber-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
          {receipt.error || 'Lecture du ticket impossible.'}
        </p>
      )}
      <p className="flex items-start gap-2 text-sm text-slate-700">
        <Clock size={16} className="mt-0.5 shrink-0 text-slate-400" aria-hidden />
        Ticket gardé — je le rattache dès que le paiement apparaît dans Qonto
      </p>
      <button
        type="button"
        onClick={onClose}
        className="min-h-12 w-full rounded-xl bg-slate-800 text-base font-semibold text-white"
      >
        OK
      </button>
      <DismissLink onDismiss={onDismiss} pending={dismissing} />
    </div>
  );
}

function UnmatchedState({ receipt, onClose, onDismiss, dismissing }) {
  return (
    <div className="space-y-4">
      <ExtractedSummary ex={receipt.extracted} />
      <p className="text-sm text-slate-700">
        Aucune transaction correspondante n'a été trouvée pour ce ticket.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="min-h-12 w-full rounded-xl bg-slate-800 text-base font-semibold text-white"
      >
        OK
      </button>
      <DismissLink onDismiss={onDismiss} pending={dismissing} />
    </div>
  );
}

function SheetBody({ receipt, photoUrl, uploading, uploadError, onRetry, onClose }) {
  const [result, setResult] = useState(null);
  const dismiss = useDismissReceipt();
  const shown = result ?? receipt;
  const imageSrc = photoUrl ?? (receipt ? apiUrl(`/receipts/${receipt.id}/image`) : null);

  async function handleDismiss() {
    try {
      await dismiss.mutateAsync(receipt.id);
      onClose();
    } catch { /* erreur affichée ci-dessous */ }
  }
  const dismissProps = { onDismiss: handleDismiss, dismissing: dismiss.isPending };

  let content;
  if (uploadError) {
    content = (
      <div className="space-y-4">
        <p role="alert" className="text-sm text-red-600">
          {uploadError.message || 'Envoi du ticket impossible.'}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="min-h-12 w-full rounded-xl bg-slate-800 text-base font-semibold text-white"
        >
          Réessayer
        </button>
      </div>
    );
  } else if (uploading || !shown) {
    content = (
      <div role="status" className="flex items-center justify-center gap-2 py-4 text-sm text-slate-600">
        <Loader2 size={18} className="animate-spin" aria-hidden />
        Lecture du ticket…
      </div>
    );
  } else if (shown.status === 'uploaded') {
    content = <SuccessState receipt={shown} onClose={onClose} />;
  } else if (shown.status === 'to_confirm') {
    content = (
      <ConfirmForm receipt={shown} onConfirmed={setResult} {...dismissProps} />
    );
  } else if (shown.status === 'pending') {
    content = <PendingState receipt={shown} onClose={onClose} {...dismissProps} />;
  } else {
    content = <UnmatchedState receipt={shown} onClose={onClose} {...dismissProps} />;
  }

  const showImage = imageSrc && shown?.status !== 'uploaded';
  return (
    <>
      {showImage && (
        <img
          src={imageSrc}
          alt="Photo du ticket"
          className="mb-4 h-32 w-full rounded-xl bg-slate-100 object-contain"
        />
      )}
      {content}
      {dismiss.error && <p role="alert" className="mt-2 text-sm text-red-600">{dismiss.error.message}</p>}
    </>
  );
}

export default function ReceiptSheet({ open, onClose, receipt, photoUrl, uploading = false, uploadError, onRetry }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center lg:items-center">
      <div className="absolute inset-0 bg-black/30" aria-hidden onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Ticket de paiement"
        className="relative max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-2xl lg:max-w-lg lg:rounded-2xl"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-4 pb-2 pt-3">
          <h2 className="text-base font-semibold text-slate-900">Ticket de paiement</h2>
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 flex h-11 w-11 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
            aria-label="Fermer"
          >
            <X size={20} aria-hidden />
          </button>
        </div>
        <div className="px-4">
          <SheetBody
            key={receipt?.id ?? 'new'}
            receipt={receipt}
            photoUrl={photoUrl}
            uploading={uploading}
            uploadError={uploadError}
            onRetry={onRetry}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
}
